import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import * as d3 from "d3";
import { apiFetch } from "../../api/apiClient";
import { extractGraphMetricsAggregates, normalizeDayGraphPayload } from "../../api/util";
import { createPortfolioDateFormatter, DEFAULT_TIME_ZONE } from "../../utils/dateTime";

// Fast deterministic hash → [0, 1). Used to build stable placeholder
// percentages so the overlay line doesn't jitter between renders.
function hashToUnit(str) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // mulberry32 finisher for better distribution
  h = (h + 0x6d2b79f5) >>> 0;
  let t = h;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

/**
 * Daily bar chart with two variants and two durations:
 *  - variant `'diverging'`: each bar spans `pos`→`neg` with a symmetric Y axis.
 *  - variant `'positive'` : each bar spans `0`→`value` on a [0, max] Y axis.
 *  - duration `'week'`    : 7 days, two-line `dayLabel + dateLabel` ticks.
 *  - duration `'month'`   : daily bars with day-of-month numeric labels.
 *
 * The X-axis is driven entirely by the rows the API returns (one bar per row,
 * in payload order), so the chart works for any rolling window – e.g. last 7
 * days or last 30 days ending today – without needing a calendar scaffold.
 *
 * @param {object} props
 * @param {'week'|'month'} props.duration
 * @param {'diverging'|'positive'} props.variant
 * @param {{ start: string, end: string }} props.gradient   Top/bottom gradient stops.
 * @param {string} props.wrapperClassName
 * @param {React.ReactNode} [props.headerLeft]
 * @param {React.ReactNode} [props.headerRight]
 * @param {string} [props.dataEndpoint]                     Backend URL for normalized day graph payload.
 * @param {string} props.tooltipUnit                        Unit appended to tooltip values (e.g. `'MWh'`).
 * @param {{ pos?: string, neg?: string, value?: string }} props.tooltipLabels
 * @param {function} [props.onTooltip]                      `(state) => void` setter for the page-level tooltip.
 * @param {function} [props.onAggregatesLoaded]             `(aggregates) => void` called after a successful endpoint fetch.
 * @param {number} [props.height]                           Defaults to 140.
 * @param {number} [props.padding]                          d3 scaleBand padding; defaults per duration.
 * @param {number} [props.ticks]                            Y-axis tick count; defaults to 5.
 * @param {[number, number]} [props.domain]               Explicit Y domain. Overrides auto-scaling and `fixedScale` when set.
 * @param {number[]} [props.tickValues]                   Explicit Y-axis tick values (e.g. asymmetric grid axis).
 * @param {number} [props.fixedScale]                       Lock Y axis: `[-fixedScale, fixedScale]` (diverging) or `[0, fixedScale]` (positive).
 * @param {object} [props.percentLine]                      Enable a secondary % overlay line.
 *   @param {[number, number]} [props.percentLine.domain]     Right-axis domain (e.g. `[0, 100]` or `[-100, 100]`). Defaults to `[0, 100]`.
 *   @param {string}            [props.percentLine.color]     Stroke / dot color. Defaults to `'#1f2937'`.
 *   @param {number}            [props.percentLine.strokeWidth] Stroke width in px. Defaults to 2.
 *   @param {number}            [props.percentLine.dotRadius]   Dot radius in px. Defaults to 3.
 *   @param {string}            [props.percentLine.label]    Tooltip label. Defaults to `'Percent'`.
 *   @param {string}            [props.percentLine.unit]     Axis + tooltip unit suffix. Defaults to `'%'`.
 *   @param {number}            [props.percentLine.ticks]    Right-axis tick count. Defaults to 5.
 *   @param {string}            [props.percentLine.seed]     Optional seed to vary placeholder values between charts.
 */
const SingleBarChart = ({
  duration,
  variant,
  gradient,
  wrapperClassName = "",
  headerLeft = null,
  headerRight = null,
  dataEndpoint = "",
  tooltipUnit = "",
  tooltipLabels = {},
  onTooltip,
  onAggregatesLoaded,
  height = 140,
  padding,
  ticks = 5,
  domain = null,
  tickValues = null,
  fixedScale = null,
  percentLine = null,
  timeZone = DEFAULT_TIME_ZONE,
}) => {
  const dateFormatter = useMemo(
    () => createPortfolioDateFormatter(timeZone),
    [timeZone],
  );
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const onTooltipRef = useRef(onTooltip);
  onTooltipRef.current = onTooltip;
  const onAggregatesLoadedRef = useRef(onAggregatesLoaded);
  onAggregatesLoadedRef.current = onAggregatesLoaded;
  const reactId = useId();
  const gradientId = `bar-grad-${reactId.replace(/:/g, "")}`;

  // Keyboard navigation: which bar is selected and whether arrow-key driven
  // navigation is currently active. Refs (not state) so that imperative d3
  // handlers can read/write without forcing React re-renders or redraws.
  const keyboardIndexRef = useRef(0);
  const keyboardModeRef = useRef(false);

  const [width, setWidth] = useState(0);
  const [data, setData] = useState([]);

  const isMonth = duration === "month";
  const isDiverging = variant === "diverging";

  // Normalize the optional percent-line config once so the draw effect
  // can rely on stable defaults without re-checking every field.
  const percentCfg = useMemo(() => {
    if (!percentLine) return null;
    const domain = Array.isArray(percentLine.domain) && percentLine.domain.length === 2
      ? percentLine.domain
      : [0, 100];
    return {
      domain,
      color: percentLine.color ?? "#1f2937",
      strokeWidth: percentLine.strokeWidth ?? 2,
      dotRadius: percentLine.dotRadius ?? 3,
      label: percentLine.label ?? "Percent",
      unit: percentLine.unit ?? "%",
      ticks: percentLine.ticks ?? 5,
      seed: percentLine.seed ?? "pct",
    };
  }, [percentLine]);

  // Placeholder percent values, deterministically derived from each row's
  // date key so they remain stable across redraws (resize, tooltip, etc.).
  // When/if a real backend field arrives, swap `hashToUnit` for `row.pct`.
  const percentByKey = useMemo(() => {
    if (!percentCfg || data.length === 0) return null;
    const [pmin, pmax] = percentCfg.domain;
    const span = pmax - pmin;
    const map = new Map();
    data.forEach(d => {
      const r = hashToUnit(`${percentCfg.seed}:${d.key}`);
      map.set(d.key, pmin + r * span);
    });
    return map;
  }, [percentCfg, data]);

  // Fetch + normalize. The normalizer's `'solar'` mode parses `value`; any
  // other type parses `pos`/`neg`, so we just key off variant.
  useEffect(() => {
    if (!dataEndpoint) {
      setData([]);
      return undefined;
    }

    let cancelled = false;
    const normalizerType = isDiverging ? "grid" : "solar";

    const load = async () => {
      try {
        const raw = await apiFetch(dataEndpoint);
        const normalized = normalizeDayGraphPayload(raw, normalizerType);
        if (!cancelled) {
          setData(normalized);
          onAggregatesLoadedRef.current?.(extractGraphMetricsAggregates(raw));
        }
      } catch (apiError) {
        console.warn(`API fetch failed for ${dataEndpoint}:`, apiError);
        if (!cancelled) {
          setData([]);
          onAggregatesLoadedRef.current?.(null);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [dataEndpoint, isDiverging]);

  // Responsive width via ResizeObserver (handles sidebar open/close).
  useEffect(() => {
    if (!containerRef.current) return undefined;
    const el = containerRef.current;

    const update = () => {
      requestAnimationFrame(() => {
        if (containerRef.current) {
          setWidth(containerRef.current.getBoundingClientRect().width);
        }
      });
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);

    el.addEventListener("transitionend", update);
    return () => {
      ro.disconnect();
      el.removeEventListener("transitionend", update);
    };
  }, []);

  // Draw / redraw on data or size change.
  useEffect(() => {
    if (!width || !svgRef.current || data.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    // Gradient def
    const defs = svg.append("defs");
    const grad = defs
      .append("linearGradient")
      .attr("id", gradientId)
      .attr("x1", "0")
      .attr("y1", "0")
      .attr("x2", "0")
      .attr("y2", "1");
    grad.append("stop").attr("offset", "0%").attr("stop-color", gradient.start);
    grad.append("stop").attr("offset", "100%").attr("stop-color", gradient.end);

    // Bottom margin: two-line labels (week) need more room than day-number text (month).
    // Left/right margins grow when axis tick labels include a unit suffix (e.g. "MWh").
    const margin = {
      top: 10,
      right: percentCfg ? 44 : 16,
      bottom: isMonth ? 42 : 46,
      left: tooltipUnit ? 68 : 40,
    };
    const innerW = Math.max(0, width - margin.left - margin.right);
    const innerH = Math.max(0, height - margin.top - margin.bottom);
    const bandPadding = padding ?? (isMonth ? 0.15 : 0.25);

    const gRoot = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const gGrid = gRoot.append("g").attr("class", "grid-y");
    const gYAxis = gRoot.append("g").attr("class", "y-axis");
    const gXAxis = gRoot.append("g").attr("class", "x-axis");
    const gBars = gRoot.append("g").attr("class", "bars");
    const gLabels = gRoot.append("g").attr("class", "labels");
    const gPercent = gRoot.append("g").attr("class", "percent-line");
    const gYAxisR = gRoot.append("g").attr("class", "y-axis-right");
    const gCrosshair = gRoot.append("g").attr("class", "crosshair");
    const gOverlay = gRoot.append("g").attr("class", "overlay");

    // Y scale + domain depend on variant.
    const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
    let yMin;
    let yMax;
    let y;
    if (isDiverging) {
      if (Array.isArray(domain) && domain.length === 2) {
        [yMin, yMax] = domain;
      } else {
        const maxAbs =
          d3.max(data, d => Math.max(Math.abs(d.pos), Math.abs(d.neg))) || 1;
        const absMax = fixedScale != null ? fixedScale : Math.ceil(maxAbs * 1.1);
        yMin = -absMax;
        yMax = absMax;
      }
      y = d3.scaleLinear().domain([yMin, yMax]).range([innerH, 0]);
    } else {
      if (Array.isArray(domain) && domain.length === 2) {
        [yMin, yMax] = domain;
      } else {
        const maxVal = d3.max(data, d => d.value) || 1;
        yMax = fixedScale != null ? fixedScale : Math.ceil(maxVal * 1.1);
        yMin = 0;
      }
      y = d3.scaleLinear().domain([yMin, yMax]).range([innerH, 0]);
      if (!domain) {
        y.nice();
      }
    }

    // X scale: one band per data row, in payload order. This lets the chart
    // render any rolling window (e.g. last 7 days or last 30 days ending
    // today) without a calendar-month scaffold.
    const xKeys = data.map(d => d.key);
    const x = d3
      .scaleBand()
      .domain(xKeys)
      .range([0, innerW])
      .padding(bandPadding);

    const barW = x.bandwidth();

    // Horizontal grid lines (zero line emphasized when present).
    const gridTicks = y.ticks(isDiverging ? 6 : 5);
    gGrid
      .selectAll("line.grid")
      .data(gridTicks, d => d)
      .join("line")
      .attr("class", "grid")
      .attr("x1", 0)
      .attr("x2", innerW)
      .attr("y1", y)
      .attr("y2", y)
      .attr("stroke", d =>
        d === 0 ? "rgba(0,0,0,0.6)" : "rgba(0,0,0,0.08)"
      )
      .attr("stroke-width", d => (d === 0 ? 1.2 : 1))
      .attr("stroke-dasharray", d => (d === 0 ? "0" : "2,3"));

    // Y axis (left)
    const yAxis = d3
      .axisLeft(y)
      .tickSize(0)
      .tickFormat(d => (tooltipUnit ? `${d} ${tooltipUnit}` : `${d}`));
    if (Array.isArray(tickValues) && tickValues.length > 0) {
      yAxis.tickValues(tickValues);
    } else {
      yAxis.ticks(ticks);
    }
    gYAxis.call(yAxis);
    gYAxis.select(".domain").attr("stroke", "rgba(0,0,0,0.55)");
    gYAxis
      .selectAll("text")
      .attr("font-size", 11)
      .attr("fill", "rgba(0,0,0,0.55)")
      .style("font-weight", "bold");
    gYAxis.selectAll(".tick line").remove();

    // X axis labels: week uses axisBottom with two-line labels; month uses
    // a manual day-number text layer because the axis ticks would crowd.
    // The day number comes from each row's own date so a rolling window
    // that crosses a calendar boundary still labels correctly (e.g. ... 30, 1, 2 ...).
    if (isMonth) {
      gLabels
        .selectAll("text.daynum")
        .data(data, d => d.key)
        .join("text")
        .attr("class", "daynum")
        .attr("text-anchor", "middle")
        .attr("font-size", 9.5)
        .attr("fill", "#444")
        .attr("x", d => (x(d.key) ?? 0) + barW / 2)
        .attr("y", innerH + 16)
        .text(d => d.date.getDate());
    } else {
      const xAxis = d3
        .axisBottom(x)
        .tickSizeOuter(0)
        .tickFormat(key => {
          const d = data.find(dd => dd.key === key);
          return d ? `${d.dayLabel} ${d.dateLabel}` : key;
        });

      gXAxis
        .attr("transform", `translate(0, ${innerH})`)
        .call(xAxis);
      gXAxis.select(".domain").attr("stroke", "rgba(0,0,0,0.3)");
      gXAxis
        .selectAll("text")
        .attr("font-size", 11)
        .attr("fill", "#444");
    }

    // Tooltip content per variant. When a percent line is enabled, append
    // its rounded value to the bottom of the tooltip block.
    const tooltipContentFor = d => {
      const dateStr = dateFormatter.formatDate(d.date, {
        month: "short",
        weekday: "short",
        day: "2-digit",
        hour12: false,
      });

      const pctNode = percentCfg && percentByKey?.has(d.key) ? (
        <>
          <br />
          <strong>{percentCfg.label}:</strong>{" "}
          {Math.round(percentByKey.get(d.key) * 10) / 10}
          {percentCfg.unit}
        </>
      ) : null;

      if (isDiverging) {
        const posLabel = tooltipLabels.pos ?? "Positive";
        const negLabel = tooltipLabels.neg ?? "Negative";
        return (
          <>
            <strong>Date:</strong> {dateStr}
            <br />
            <strong>{posLabel}:</strong> {d.pos} {tooltipUnit}
            <br />
            <strong>{negLabel}:</strong> {d.neg} {tooltipUnit}
            <br />
            <strong>Diff:</strong> {d.pos - d.neg} {tooltipUnit}
            {pctNode}
          </>
        );
      }

      const valueLabel = tooltipLabels.value ?? "Value";
      return (
        <>
          <strong>Date:</strong> {dateStr}
          <br />
          <strong>{valueLabel}:</strong> {d.value} {tooltipUnit}
          {pctNode}
        </>
      );
    };

    // Bars: one rect per data point (week) or one per month day with data.
    // Pointer events are routed through the overlay below so the crosshair
    // can snap to the nearest bar even when the cursor isn't directly on it.
    const barFill = `url(#${gradientId})`;
    const bars = gBars
      .selectAll("rect.bar")
      .data(data, d => d.key)
      .join(enter =>
        enter
          .append("rect")
          .attr("class", "bar")
          .attr("rx", 3)
      )
      .attr("x", d => x(d.key) ?? 0)
      .attr("width", barW)
      .attr("y", d =>
        isDiverging ? y(clamp(d.pos, yMin, yMax)) : y(clamp(d.value, yMin, yMax))
      )
      .attr("height", d =>
        isDiverging
          ? Math.max(
              0,
              y(clamp(d.neg, yMin, yMax)) - y(clamp(d.pos, yMin, yMax))
            )
          : Math.max(0, y(yMin) - y(clamp(d.value, yMin, yMax)))
      )
      .attr("fill", barFill)
      .style("pointer-events", "none");

    // For diverging, draw a thin white separator at zero so the bar gradient
    // is visually broken at the axis (matches the original monthly look).
    if (isDiverging) {
      gGrid
        .append("line")
        .attr("class", "zero")
        .attr("x1", 0)
        .attr("x2", innerW)
        .attr("y1", y(0))
        .attr("y2", y(0))
        .attr("stroke", "rgba(255,255,255,0.7)")
        .attr("stroke-width", 1);
    }

    // Captured by the percent-line block below when enabled; used by the
    // crosshair handler to enlarge the dot at the active index and to
    // anchor the tooltip above the dot when it sits higher than the bar.
    let pctDots = null;
    let pctDotR = 0;
    let yPctScale = null;

    // Optional secondary axis + line overlay. Driven entirely by the
    // pre-built `percentByKey` map so the values stay deterministic across
    // resizes and re-renders.
    if (percentCfg && percentByKey && percentByKey.size > 0) {
      const [pmin, pmax] = percentCfg.domain;
      const yPct = d3.scaleLinear().domain([pmin, pmax]).range([innerH, 0]);
      yPctScale = yPct;

      const yAxisRight = d3
        .axisRight(yPct)
        .ticks(percentCfg.ticks)
        .tickSize(0)
        .tickFormat(d => `${d}${percentCfg.unit}`);

      gYAxisR
        .attr("transform", `translate(${innerW}, 0)`)
        .call(yAxisRight);
      gYAxisR.select(".domain").attr("stroke", "rgba(0,0,0,0.55)");
      gYAxisR
        .selectAll("text")
        .attr("font-size", 11)
        .attr("fill", percentCfg.color);
      gYAxisR.selectAll(".tick line").remove();

      // Build line points: one per data row (not per scaffold day) so the
      // line skips missing month entries cleanly.
      const pctPoints = data
        .filter(d => percentByKey.has(d.key) && x(d.key) != null)
        .map(d => ({
          key: d.key,
          cx: (x(d.key) ?? 0) + barW / 2,
          cy: yPct(percentByKey.get(d.key)),
        }));

      const lineGen = d3
        .line()
        .x(p => p.cx)
        .y(p => p.cy)
        .curve(d3.curveMonotoneX);

      gPercent
        .append("path")
        .datum(pctPoints)
        .attr("fill", "none")
        .attr("stroke", percentCfg.color)
        .attr("stroke-width", percentCfg.strokeWidth)
        .attr("stroke-linecap", "round")
        .attr("stroke-linejoin", "round")
        .attr("vector-effect", "non-scaling-stroke")
        .attr("d", lineGen);

      pctDotR = percentCfg.dotRadius;
      pctDots = gPercent
        .selectAll("circle.pct-dot")
        .data(pctPoints, p => p.key)
        .join("circle")
        .attr("class", "pct-dot")
        .attr("cx", p => p.cx)
        .attr("cy", p => p.cy)
        .attr("r", pctDotR)
        .attr("fill", percentCfg.color)
        .style("pointer-events", "none");
    }

    // Vertical crosshair line + transparent overlay rect for hit testing.
    // The overlay snaps the cursor to the nearest bar center via bisect so
    // tooltips and the crosshair line track even between bars.
    const crosshair = gCrosshair
      .append("line")
      .attr("stroke", "rgba(0,0,0,0.45)")
      .attr("stroke-width", 1)
      .attr("stroke-dasharray", "2,3")
      .attr("y1", 0)
      .attr("y2", innerH)
      .style("opacity", 0)
      .style("pointer-events", "none");

    const centers = data
      .map(d => {
        const xb = x(d.key);
        return xb == null ? null : xb + barW / 2;
      })
      .filter(c => c != null);
    const centerToDatum = data.filter(d => x(d.key) != null);

    const resetDots = () => {
      if (pctDots) pctDots.attr("r", pctDotR);
    };

    const resetBars = () => {
      bars.attr("fill", barFill);
    };

    // Anchor the page-level tooltip to the snapped bar (centered on the
    // bar, just above its top) rather than following the mouse pointer.
    // This stabilizes the tooltip while the cursor moves within a single
    // bar's snap region and is touch-friendly on mobile.
    const showTooltipForDatum = (d, centerX) => {
      const tip = onTooltipRef.current;
      if (!tip) return;
      const svgEl = svgRef.current;
      if (!svgEl) return;

      // Match the clamped bar geometry so the tooltip stays on-screen when
      // values exceed the fixed axis domain (e.g. grid bars above 700 MWh).
      const barTopY = isDiverging
        ? y(clamp(d.pos, yMin, yMax))
        : y(clamp(d.value, yMin, yMax));
      let anchorY = barTopY;
      if (yPctScale && percentByKey && percentByKey.has(d.key)) {
        const pctY = yPctScale(percentByKey.get(d.key));
        if (pctY < anchorY) anchorY = pctY;
      }

      const rect = svgEl.getBoundingClientRect();
      tip({
        visible: true,
        content: tooltipContentFor(d),
        x: rect.left + margin.left + centerX,
        y: rect.top + margin.top + anchorY - 12,
      });
    };

    // Show the crosshair + highlight bar + tooltip snapped to a specific
    // index. Used by both the mouse hover handlers and the keyboard
    // navigation handlers. `idx` is into the `centers` / `centerToDatum`
    // arrays (rows with a valid x band).
    const showSelectionByIndex = idx => {
      if (!centers.length) return;
      const clamped = Math.max(0, Math.min(centers.length - 1, idx));
      keyboardIndexRef.current = clamped;
      const d = centerToDatum[clamped];
      if (!d) return;

      crosshair
        .attr("x1", centers[clamped])
        .attr("x2", centers[clamped])
        .style("opacity", 1);

      resetDots();
      if (pctDots) {
        pctDots
          .filter(p => p.key === d.key)
          .attr("r", pctDotR + 3);
      }

      resetBars();
      bars
        .filter(b => b.key === d.key)
        .attr("fill", gradient.start);

      showTooltipForDatum(d, centers[clamped]);
    };

    const hideSelection = () => {
      crosshair.style("opacity", 0);
      resetDots();
      resetBars();
      const tip = onTooltipRef.current;
      if (!tip) return;
      tip(t => ({ ...t, visible: false }));
    };

    gOverlay
      .append("rect")
      .attr("width", innerW)
      .attr("height", innerH)
      .attr("fill", "transparent")
      .style("cursor", "crosshair")
      .on("pointerenter", function (event) {
        if (!centers.length) return;
        keyboardModeRef.current = false;
        const [mx] = d3.pointer(event);
        const idx = Math.max(
          0,
          Math.min(centers.length - 1, d3.bisectCenter(centers, mx))
        );
        showSelectionByIndex(idx);
      })
      .on("pointermove", function (event) {
        if (!centers.length) return;
        // Any real mouse movement on the chart kicks us out of keyboard
        // navigation mode and back into mouse-follow mode.
        keyboardModeRef.current = false;
        const [mx] = d3.pointer(event);
        const idx = Math.max(
          0,
          Math.min(centers.length - 1, d3.bisectCenter(centers, mx))
        );
        showSelectionByIndex(idx);
      })
      .on("pointerleave", () => {
        // If the chart still has keyboard focus when the mouse leaves, fall
        // back to keyboard-navigation mode so the user can keep arrow-keying.
        if (svgRef.current && document.activeElement === svgRef.current) {
          keyboardModeRef.current = true;
          showSelectionByIndex(keyboardIndexRef.current);
        } else {
          hideSelection();
        }
      });

    const svgEl = svgRef.current;

    const handleKeyDown = e => {
      if (!keyboardModeRef.current) return;
      if (!centers.length) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (keyboardIndexRef.current > 0) {
          showSelectionByIndex(keyboardIndexRef.current - 1);
        }
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (keyboardIndexRef.current < centers.length - 1) {
          showSelectionByIndex(keyboardIndexRef.current + 1);
        }
      }
    };

    const handleFocus = () => {
      if (!centers.length) return;
      keyboardModeRef.current = true;
      showSelectionByIndex(keyboardIndexRef.current);
    };

    const handleBlur = () => {
      keyboardModeRef.current = false;
      hideSelection();
    };

    svgEl.addEventListener("keydown", handleKeyDown);
    svgEl.addEventListener("focus", handleFocus);
    svgEl.addEventListener("blur", handleBlur);

    // If the chart was already focused when this effect re-ran (e.g. data
    // updated while the user was navigating with the keyboard), re-enter
    // keyboard mode and re-render the selection so the crosshair persists.
    if (document.activeElement === svgEl) {
      handleFocus();
    }

    return () => {
      svgEl.removeEventListener("keydown", handleKeyDown);
      svgEl.removeEventListener("focus", handleFocus);
      svgEl.removeEventListener("blur", handleBlur);
    };
  }, [
    width,
    data,
    isMonth,
    isDiverging,
    height,
    padding,
    ticks,
    domain,
    tickValues,
    fixedScale,
    gradient,
    gradientId,
    tooltipLabels,
    tooltipUnit,
    percentCfg,
    percentByKey,
    dateFormatter,
  ]);

  return (
    <div
      ref={containerRef}
      className={wrapperClassName}
      style={{ width: "100%" }}
      data-duration={duration}
    >
      {(headerLeft || headerRight) && (
        <div className="header">
          <span>{headerLeft}</span>
          <span>{headerRight}</span>
        </div>
      )}
      <svg
        ref={svgRef}
        width="100%"
        height={height}
        tabIndex={0}
        role="img"
        aria-label="Daily bar chart. Use left and right arrow keys to navigate between bars."
        style={{ display: "block" }}
      />
    </div>
  );
};

export default SingleBarChart;
